import { ArrowDownRight, ArrowUpRight, Plus } from "lucide-react";

import { ScrollArea } from "@/components/ui/scroll-area";
import { formatPrice, getPriceDelta } from "@/lib/price";
import { cn } from "@/lib/utils";
import type { Product } from "@/mocks/products";

type ProductRailProps = {
  products: Product[];
  selectedKey: string;
  onSelect: (product: Product) => void;
  className?: string;
};

export function ProductRail({
  products,
  selectedKey,
  onSelect,
  className,
}: ProductRailProps): JSX.Element {
  return (
    <aside className={cn("min-w-0", className)} aria-label="Tracked products">
      <div className="mb-4 flex min-h-8 items-center justify-between gap-3">
        <span className="text-xs font-extrabold uppercase tracking-normal text-[#d8dfef]">
          Watchlist
        </span>
        <span className="flex items-center gap-2">
          <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#202a3c] px-2 text-xs font-bold text-[#dbe4f5]">
            {products.length}
          </span>
          <button
            aria-label="Add product to watchlist"
            className="grid h-7 w-7 place-items-center rounded-md border border-[#20304a] bg-[#071120] text-[#dce6f7] hover:bg-[#0b182a]"
            type="button"
          >
            <Plus className="h-4 w-4" />
          </button>
        </span>
      </div>
      <ScrollArea className="lg:max-h-[calc(100vh-178px)]">
        <div className="grid gap-3 pr-2">
          {products.map((product) => {
            const delta = getPriceDelta(product);
            const selected = product.productKey === selectedKey;
            return (
              <button
                className={cn(
                  "relative grid min-h-[106px] w-full min-w-0 grid-cols-[72px_minmax(0,1fr)] items-center gap-3 rounded-lg border border-[#1a2940] bg-[#071120] p-2.5 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] transition hover:border-[#ff4a25]/70 hover:bg-[#0a1727]",
                  selected &&
                    "border-[#ff4a25] bg-[#0c1726] shadow-[0_0_0_1px_rgba(255,74,37,0.2),0_0_28px_rgba(255,74,37,0.18)]",
                )}
                key={product.productKey}
                onClick={() => onSelect(product)}
                type="button"
              >
                <img
                  alt=""
                  className="h-[72px] w-[72px] rounded-md bg-[#111b29] object-cover"
                  src={product.imageUrl}
                />
                <span className="grid min-w-0 gap-1">
                  <strong className="line-clamp-2 text-sm font-extrabold leading-tight text-white">
                    {product.productName}
                  </strong>
                  <span className="truncate text-xs font-medium text-[#8d9aaf]">
                    {product.listings.length} listings · {product.mergeStatus}
                  </span>
                  <span className="grid min-w-0 gap-0.5 text-xs font-extrabold">
                    <span className="truncate tabular-nums text-white">
                      {formatPrice(product.latestPrice)}
                    </span>
                    {delta !== undefined ? (
                      <span
                        className={cn(
                          "inline-flex shrink-0 items-center gap-0.5 tabular-nums",
                          delta <= 0 ? "text-[#2cff6a]" : "text-[#ffb443]",
                        )}
                      >
                        {delta <= 0 ? (
                          <ArrowDownRight className="h-3.5 w-3.5" />
                        ) : (
                          <ArrowUpRight className="h-3.5 w-3.5" />
                        )}
                        {formatPrice(Math.abs(delta))}
                      </span>
                    ) : null}
                  </span>
                </span>
                <span className="absolute bottom-4 right-3 h-2.5 w-2.5 rounded-full bg-[#ff6a00] shadow-[0_0_16px_rgba(255,106,0,0.8)]" />
              </button>
            );
          })}
        </div>
      </ScrollArea>
    </aside>
  );
}
